/**
 * Turning a set of selected category keys into the chips the settings panel
 * shows above the tree.
 *
 * Ticking a category in the panel selects its whole subtree, so a single
 * click on pokemadness's "Pokemon" puts nine keys in the selection — and,
 * once categories carry a parent, nine chips each spelling out
 * "Hjem → Katalog → Pokemon → …". Rendered verbatim that block grew taller
 * than the tree it sits above. Both reductions here are lossless: a subtree
 * selected in full is named by its root, and the ancestors every chip shares
 * are stated once instead of on every chip.
 */

/** A discovered category, trimmed to what the summary reads. */
export type ChipNode = {
	key: string;
	name: string;
	parentKey: string | null;
	/** True when the last discovery run no longer saw this category. */
	removed?: boolean;
};

export type SelectionChip = {
	/** The category the chip names — the top of a fully selected subtree. */
	key: string;
	/** Its path, with the ancestors every chip shares already removed. */
	path: ChipNode[];
	/** Every selected key this chip stands for, including its own. Removing
	 *  the chip deselects all of them. */
	subtreeKeys: string[];
	/** How many selected categories the chip folds away. */
	hiddenCount: number;
	stale: boolean;
};

export type SelectionSummary = {
	chips: SelectionChip[];
	/** Ancestors common to every chip, stated once above them. */
	sharedPath: ChipNode[];
};

function ancestorPath(key: string, byKey: Map<string, ChipNode>): ChipNode[] {
	const out: ChipNode[] = [];
	const guard = new Set<string>();
	let cur = byKey.get(key);
	while (cur && !guard.has(cur.key)) {
		out.unshift(cur);
		guard.add(cur.key);
		cur = cur.parentKey ? byKey.get(cur.parentKey) : undefined;
	}
	return out;
}

export function summarizeSelection(selected: ReadonlySet<string>, nodes: readonly ChipNode[]): SelectionSummary {
	const byKey = new Map(nodes.map((n) => [n.key, n]));
	const childrenOf = new Map<string, string[]>();
	for (const n of nodes) {
		if (!n.parentKey || n.parentKey === n.key) continue;
		const siblings = childrenOf.get(n.parentKey);
		if (siblings) siblings.push(n.key);
		else childrenOf.set(n.parentKey, [n.key]);
	}

	function subtree(key: string): string[] {
		const out: string[] = [];
		const seen = new Set<string>();
		const stack = [key];
		while (stack.length > 0) {
			const k = stack.pop()!;
			if (seen.has(k)) continue;
			seen.add(k);
			out.push(k);
			for (const c of childrenOf.get(k) ?? []) stack.push(c);
		}
		return out;
	}

	const fullySelected = new Map<string, boolean>();
	function isFullySelected(key: string): boolean {
		const cached = fullySelected.get(key);
		if (cached != null) return cached;
		// Seed before recursing so a malformed cycle resolves rather than hangs.
		fullySelected.set(key, false);
		const all = subtree(key).every((k) => selected.has(k));
		fullySelected.set(key, all);
		return all;
	}

	const chips: SelectionChip[] = [];
	for (const key of selected) {
		const node = byKey.get(key);
		// A pick discovery has not reached yet has no place in the tree; the
		// sweep still honours it, but there is no path to draw for it.
		if (!node) continue;

		// Absorbed when some ancestor is selected and holds its whole subtree —
		// that ancestor's chip already stands for this one.
		let absorbed = false;
		const guard = new Set<string>([key]);
		for (
			let cur = node.parentKey ? byKey.get(node.parentKey) : undefined;
			cur && !guard.has(cur.key);
			cur = cur.parentKey ? byKey.get(cur.parentKey) : undefined
		) {
			guard.add(cur.key);
			if (selected.has(cur.key) && isFullySelected(cur.key)) {
				absorbed = true;
				break;
			}
		}
		if (absorbed) continue;

		const covered = isFullySelected(key) ? subtree(key) : [key];
		chips.push({
			key,
			path: ancestorPath(key, byKey),
			subtreeKeys: covered,
			hiddenCount: covered.length - 1,
			stale: node.removed === true,
		});
	}

	// The shared prefix is capped one short of the shortest path, so every
	// chip keeps at least its own name.
	let shared = 0;
	if (chips.length > 0) {
		const limit = Math.min(...chips.map((c) => c.path.length)) - 1;
		while (shared < limit) {
			const here = chips[0]!.path[shared]!.key;
			if (!chips.every((c) => c.path[shared]?.key === here)) break;
			shared++;
		}
	}

	const sharedPath = shared > 0 ? chips[0]!.path.slice(0, shared) : [];
	for (const chip of chips) chip.path = chip.path.slice(shared);

	chips.sort((a, b) =>
		a.path
			.map((n) => n.name)
			.join(" → ")
			.localeCompare(b.path.map((n) => n.name).join(" → "), "nb"),
	);

	return { chips, sharedPath };
}
